import React from 'react';
import {Alert, AlertTitle, Box, TextField, Typography} from '@mui/material';
import {Control, FieldValues} from 'react-hook-form';
import AppMultiSelect from '../../../../Component/Common/Components/FormValidationInputs/AppMultiSelect';
import {GetAllScorecardsResponse} from '../../../../Component/Model/Dto/ScorecardLicense';
import {RequestTracker} from '../../../../Component/Model/Dto/Staging/RequestTracker';
import {SalaryBatch} from '../../../../Component/Model/Dto/Staging/SalaryBatch';
import {getIOptionsArray} from '../../../../Helpers/arrayHelper';
import colors from '../../../../assets/theme/base/colors';
import {ImportStep, stepFieldWidth} from './ImportStep';

interface ICoverageVerdict {
    verdict: string;
    textColour: string;
    borderColour: string;
}

interface IProps {
    control: Control<FieldValues>;
    selectedOrganisation: number;
    scorecardData: GetAllScorecardsResponse;
    selectedScorecardIds: string[];
    findScorecardName: (scorecardId: string) => string;
    requestTracker?: RequestTracker.RequestTrackerDetail;
    employeeRecordCount?: number;
    includeSalaries: boolean;
    hasBEEImportRole: boolean;
    hasEEImportRole: boolean;
    disabled: boolean;
}

export const DestinationStep: React.FC<IProps> =
    ({
         control,
         selectedOrganisation,
         scorecardData,
         selectedScorecardIds,
         findScorecardName,
         requestTracker,
         employeeRecordCount,
         includeSalaries,
         hasBEEImportRole,
         hasEEImportRole,
         disabled
     }) => {

        const beeScorecards = scorecardData.beeScorecards ?? [];
        const eeScorecards = scorecardData.eeScorecards ?? [];
        const hasScorecards = beeScorecards.length > 0 || eeScorecards.length > 0;
        const totalSalaries = requestTracker?.salaryBatch?.totalRecords;
        const selectedCoverage = (requestTracker?.salaryCoverage ?? [])
            .filter(coverage => selectedScorecardIds.includes(coverage.scorecardId));

        const describeCoverage = (coverage?: SalaryBatch.ScorecardCoverage): ICoverageVerdict => {
            const parts: string[] = [];
            if (employeeRecordCount != null) {
                parts.push(`${employeeRecordCount} employees`);
            }

            if (!includeSalaries) {
                parts.push('salaries not included');
                return {
                    verdict: parts.join(' · '),
                    textColour: colors.ui.secondaryText,
                    borderColour: colors.ui.naBorder
                };
            }

            if (!coverage || totalSalaries == null) {
                parts.push('no salary data fetched');
                return {
                    verdict: parts.join(' · '),
                    textColour: colors.ui.secondaryText,
                    borderColour: colors.ui.naBorder
                };
            }

            parts.push(`${coverage.recordsInPeriod} of ${totalSalaries} salaries`);

            if (coverage.recordsOutsidePeriod > 0) {
                return {verdict: parts.join(' · '), textColour: 'warning.dark', borderColour: 'warning.main'};
            }

            return {verdict: parts.join(' · '), textColour: 'success.dark', borderColour: 'success.main'};
        };

        const emptyScorecardField = (label: string) => (
            <Box sx={{maxWidth: stepFieldWidth}}>
                <TextField
                    fullWidth
                    disabled
                    size="small"
                    label={label}
                    value="None linked for this organisation"/>
            </Box>
        );

        const renderCoverageRow = (scorecardId: string) => {
            const coverage = selectedCoverage.find(item => item.scorecardId === scorecardId);
            const {verdict, textColour, borderColour} = describeCoverage(coverage);

            return (
                <Box
                    key={scorecardId}
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'baseline',
                        columnGap: 2,
                        rowGap: 0.5,
                        p: 1.5,
                        border: `1px solid ${colors.ui.border}`,
                        borderLeftWidth: 3,
                        borderLeftStyle: 'solid',
                        borderLeftColor: borderColour,
                        borderRadius: '5px'
                    }}>
                    <Typography variant="button" fontWeight="bold" sx={{color: colors.dark.main}}>
                        {findScorecardName(scorecardId)}
                    </Typography>
                    {coverage &&
                        <Typography variant="caption" color="secondary">
                            {coverage.periodStart} to {coverage.periodEnd}
                        </Typography>
                    }
                    <Typography variant="caption" fontWeight="bold" sx={{ml: 'auto', color: textColour}}>
                        {verdict}
                    </Typography>
                </Box>
            );
        };

        const renderCoverageAlert = () => {
            if (!includeSalaries || selectedCoverage.length === 0) {
                return null;
            }

            const manyScorecards = selectedCoverage.length > 1;

            if (selectedCoverage.every(coverage => coverage.recordsInPeriod === 0)) {
                return (
                    <Alert severity="warning">
                        <AlertTitle>
                            No salary records fall inside the reporting period
                            {manyScorecards ? 's you selected' : ' you selected'}
                        </AlertTitle>
                        The salary months that were fetched lie outside
                        {manyScorecards ? ' every scorecard selected' : " this scorecard's period"}. They stay in
                        staging and can still be imported into a scorecard that reports on those months.
                    </Alert>
                );
            }

            if (selectedCoverage.some(coverage => coverage.recordsOutsidePeriod > 0)) {
                return (
                    <Alert severity="warning">
                        Not all of the fetched salary records fall inside the scorecards you selected. Each scorecard
                        receives only the salary months inside its own reporting period.
                    </Alert>
                );
            }

            return null;
        };

        const renderDestination = () => {
            if (selectedOrganisation <= 0) {
                return (
                    <Typography variant="caption" color="secondary">
                        Choose an organisation in step 1.
                    </Typography>
                );
            }

            return (
                <>
                    {beeScorecards.length > 0 &&
                        <Box sx={{maxWidth: stepFieldWidth}}>
                            <AppMultiSelect
                                disabled={disabled || !hasBEEImportRole}
                                control={control}
                                hintText="Search or select an option"
                                name="beeScorecards"
                                label="BEE Scorecards"
                                options={getIOptionsArray(beeScorecards, "scorecardId", "scorecardName")}
                                size="small"/>
                        </Box>
                    }
                    {beeScorecards.length === 0 && hasScorecards && emptyScorecardField('BEE Scorecards')}
                    {beeScorecards.length > 0 && !hasBEEImportRole &&
                        <Typography variant="caption" sx={{color: colors.error.main}}>
                            You don't have permission to edit BEE People
                        </Typography>
                    }

                    {eeScorecards.length > 0 &&
                        <Box sx={{maxWidth: stepFieldWidth}}>
                            <AppMultiSelect
                                disabled={disabled || !hasEEImportRole}
                                control={control}
                                hintText="Search or select an option"
                                name="eeScorecards"
                                label="EE Scorecards"
                                options={getIOptionsArray(eeScorecards, "scorecardId", "scorecardName")}
                                size="small"/>
                        </Box>
                    }
                    {eeScorecards.length === 0 && hasScorecards && emptyScorecardField('EE Scorecards')}
                    {eeScorecards.length > 0 && !hasEEImportRole &&
                        <Typography variant="caption" sx={{color: colors.error.main}}>
                            You don't have permission to edit EE People
                        </Typography>
                    }

                    {!hasScorecards &&
                        <Typography variant="caption" sx={{color: colors.error.main}}>
                            You don’t have access to any scorecards linked to this organisation. Please contact your
                            administrator to request access or have one assigned
                        </Typography>
                    }

                    {selectedScorecardIds.map(scorecardId => renderCoverageRow(scorecardId))}

                    {renderCoverageAlert()}
                </>
            );
        };

        return (
            <ImportStep
                stepNumber={3}
                title="Destination"
                description="A scorecard only receives the salary months inside its own reporting period.">
                {renderDestination()}
            </ImportStep>
        );
    };
